import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Typography from "@material-ui/core/Typography";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

const useStyles = makeStyles((theme) => ({
    root: {
        minWidth: 0,
        maxWidth: "100%",
        padding: "2px 4px",
        marginTop: 2,
        textTransform: "none",
        fontWeight: 500,
        fontSize: 13,
        lineHeight: 1.35,
        letterSpacing: 0,
        borderRadius: 4,
        justifyContent: "flex-start",
        whiteSpace: "normal",
        textAlign: "left",
    },
    onDark: {
        color: "rgba(255,255,255,0.82)",
        "&:hover": {
            backgroundColor: "rgba(255,255,255,0.08)",
        },
    },
    prominent: {
        "&&": {
            marginLeft: 12,
            padding: "4px 6px",
            fontSize: 16,
            fontWeight: 600,
            color: "#187DFE",
            whiteSpace: "nowrap",
        },
        "&:hover": {
            backgroundColor: "rgba(24,125,254,0.12)",
        },
    },
    prominentIcon: {
        fontSize: 18,
    },
    onLight: {
        color: "rgba(0,0,0,0.68)",
        "&:hover": {
            backgroundColor: "rgba(0,0,0,0.04)",
        },
    },
    label: {
        overflowWrap: "anywhere",
    },
    icon: {
        fontSize: 16,
        marginLeft: 4,
        flexShrink: 0,
    },
    iconOnly: {
        padding: 2,
        marginLeft: 2,
    },
    body: {
        whiteSpace: "pre-wrap",
        overflowWrap: "break-word",
    },
    paper: {
        margin: theme.spacing(2),
    },
}));

/**
 * Customer-facing 5% eWallet note. Does not calculate or display a discounted price.
 */
export default function EWalletSavingsNote({ tone = "onDark", prominent = false, iconOnly = false }) {
    const classes = useStyles();
    const { t } = useTranslation("shopping");
    const [open, setOpen] = useState(false);
    const close = () => setOpen(false);
    const toneClass = prominent ? classes.prominent : tone === "onLight" ? classes.onLight : classes.onDark;

    return (
        <>
            {iconOnly ? (
                <IconButton
                    className={`${classes.iconOnly} ${toneClass}`}
                    style={{ color: tone === "onLight" ? "rgba(0,0,0,0.68)" : "rgba(255,255,255,0.9)" }}
                    onClick={() => setOpen(true)}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    aria-label={t("ewalletSavings.discountLabel")}
                    size="small"
                >
                    <InfoOutlinedIcon className={classes.icon} style={{ marginLeft: 0 }} aria-hidden="true" />
                </IconButton>
            ) : (
            <Button
                className={`${classes.root} ${toneClass}`}
                style={prominent ? { color: "#187DFE", fontSize: 16, fontWeight: 600, marginLeft: 12 } : undefined}
                onClick={() => setOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={open}
            >
                <span className={classes.label}>{t("ewalletSavings.label")}</span>
                <InfoOutlinedIcon className={`${classes.icon} ${prominent ? classes.prominentIcon : ""}`} aria-hidden="true" />
            </Button>
            )}
            <Dialog
                open={open}
                onClose={close}
                fullWidth
                maxWidth="sm"
                scroll="paper"
                aria-labelledby="ewallet-savings-title"
                classes={{ paper: classes.paper }}
            >
                <DialogTitle id="ewallet-savings-title">{t("ewalletSavings.title")}</DialogTitle>
                <DialogContent dividers>
                    <Typography className={classes.body} component="div" dangerouslySetInnerHTML={{ __html: t("ewalletSavings.body") }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={close} color="primary" variant="contained">
                        {t("home.ok")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
